import { CircularProgress, Stack } from '@mui/material';

import NoData from 'components/no-data';
import useCooperativeId from 'shared/hooks/useCooperativeId';
import formatDate from 'shared/utils/formatDate';

import { fileLoaderApi } from '../../api';
import { type Document, EntityType } from '../../model';

import { Styled } from './FilesList.styled';

type ServerFileProps = {
  document: Document;
  entityType: EntityType;
  entityId: string;
  readonly?: boolean;
};

const ServerFile = ({
  document,
  entityId,
  entityType,
  readonly,
}: ServerFileProps) => {
  const cooperativeId = useCooperativeId();

  const [downloadDocument, downloadDocumentQueryState] =
    fileLoaderApi.useLazyDownloadDocumentQuery();
  const [deleteDocument, deleteDocumentMutationState] =
    fileLoaderApi.useDeleteDocumentMutation();

  const handleDownloadDocument = () => {
    if (!cooperativeId) return;

    downloadDocument({
      cooperativeId,
      entityId,
      entityType,
      fileUrl: document.Id,
      fileName: document.Name,
    });
  };

  const handleDeleteDocument = () => {
    if (!cooperativeId) return;

    deleteDocument({
      cooperativeId,
      entityId,
      entityType,
      fileUrl: document.Id,
    });
  };

  const showDeleteButton = document.CanDelete && !readonly;

  return (
    <Styled.ListItem>
      {(downloadDocumentQueryState.isLoading ||
        downloadDocumentQueryState.isFetching) && (
        <CircularProgress color="primary" size={15} sx={{ marginRight: 1 }} />
      )}
      <Styled.TextOverflow>
        <Styled.FileName
          onClick={handleDownloadDocument}
          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
        >
          {document.Name}
        </Styled.FileName>
      </Styled.TextOverflow>
      <Stack alignItems="center" direction="row" spacing={1}>
        <Styled.FileMetadata>
          {document.CreatedOn && formatDate(new Date(document.CreatedOn))}
        </Styled.FileMetadata>
        {showDeleteButton && (
          <>
            {deleteDocumentMutationState.isLoading ? (
              <CircularProgress color="primary" size={15} sx={{ margin: 2 }} />
            ) : (
              <Styled.IconButton onClick={handleDeleteDocument}>
                <Styled.ClearIcon color="primary" />
              </Styled.IconButton>
            )}
          </>
        )}
      </Stack>
    </Styled.ListItem>
  );
};

type ServerFilesListProps = {
  documents: Document[];
  isLoading: boolean;
} & Pick<ServerFileProps, 'entityId' | 'entityType' | 'readonly'>;

const ServerFilesList = ({
  documents,
  entityId,
  entityType,
  isLoading,
  readonly,
}: ServerFilesListProps) => {
  if (isLoading) {
    return (
      <Styled.Centered>
        <CircularProgress color="primary" size={30} />
      </Styled.Centered>
    );
  }

  if (!documents.length)
    return (
      <Styled.Centered>
        <NoData text="FilesAreNotAttached" />
      </Styled.Centered>
    );

  return (
    <Styled.List>
      {documents.map((document) => (
        <ServerFile
          key={document.Id}
          document={document}
          entityId={entityId}
          entityType={entityType}
          readonly={readonly}
        />
      ))}
    </Styled.List>
  );
};

export default ServerFilesList;
